import {
  CreateReviewMember,
  DeleteReviewMember,
  UpdateReviewMember,
  AddReviewMemberPermission,
  RemoveReviewMemberPermission,
  RemoveReviewMemberPermissionFromAll,
} from '@/graphql';
import { ReviewMemberRole } from '@/enums';
import { apolloClient } from '@/plugins/apollo';

const actions = {
  async saveReviewMember(context, reviewMember) {
    const mutation = reviewMember.id
      ? UpdateReviewMember
      : CreateReviewMember;
    const response = await apolloClient.mutate({
      mutation,
      variables: { reviewMember: reviewMember.creationData() },
      optimisticResponse: reviewMember.id
        ? {
            updateReviewMember: {
              ...reviewMember,
              __typename: 'ReviewMember',
            },
          }
        : undefined,
      update: (cache, { data }) => {
        cache.modify({
          id: cache.identify(reviewMember.review),
          fields: {
            reviewers(existingReviewers, { toReference, readField }) {
              const member =
                data.createReviewMember || data.updateReviewMember;

              // Case: Add new basic reviewer.
              if (
                data.createReviewMember &&
                member.memberRole === ReviewMemberRole.REVIEWER
              ) {
                return [...existingReviewers, toReference(member)];
              }

              // Case: Reviewer is no longer a basic reviewer (e.g. made lead reviewer)
              if (
                data.updateReviewMember &&
                member.memberRole !== ReviewMemberRole.REVIEWER
              ) {
                const newReviewersList = existingReviewers?.filter(
                  (ref) => readField('id', ref) !== member.id
                );
                // Case: Lead reviewer has changed, put the previous lead back in the list.
                if (
                  member.memberRole ===
                    ReviewMemberRole.LEAD_REVIEWER &&
                  reviewMember.review.leadReviewer.id !== member.id
                ) {
                  const previousLeadAlreadyInList = newReviewersList.find(
                    (ref) =>
                      readField('id', ref) ===
                      reviewMember.review.leadReviewer.id
                  );
                  if (!previousLeadAlreadyInList) {
                    newReviewersList.push(
                      toReference(reviewMember.review.leadReviewer)
                    );
                  }
                }
                return newReviewersList;
              }

              return existingReviewers;
            },
            reviewMembers(existingReviewMembers, { toReference }) {
              // Case: New review member is added.
              if (data.createReviewMember) {
                return [
                  ...existingReviewMembers,
                  toReference(data.createReviewMember),
                ];
              }
              return existingReviewMembers;
            },
            leadReviewer(existingLeadReviewer, { toReference }) {
              const member =
                data.createReviewMember || data.updateReviewMember;

              // Case: Other member is made lead reviewer.
              if (
                member.memberRole === ReviewMemberRole.LEAD_REVIEWER
              ) {
                return toReference(member);
              }
              return existingLeadReviewer;
            },
          },
        });
      },
    });
    const newReviewMember = reviewMember.id
      ? response.data.updateReviewMember
      : response.data.createReviewMember;

    return newReviewMember;
  },
  async addPermission(context, { reviewMember, permission }) {
    const response = await apolloClient.mutate({
      mutation: AddReviewMemberPermission,
      variables: { reviewMember: reviewMember.id, permission },
      optimisticResponse: {
        addReviewMemberPermission: {
          ...reviewMember,
          memberPermissions: [
            ...reviewMember.memberPermissions,
            permission,
          ],
        },
      },
    });
    return response.data.addReviewMemberPermission;
  },
  async removePermission(context, { reviewMember, permission }) {
    const response = await apolloClient.mutate({
      mutation: RemoveReviewMemberPermission,
      variables: { reviewMember: reviewMember.id, permission },
      optimisticResponse: {
        removeReviewMemberPermission: {
          ...reviewMember,
          memberPermissions: reviewMember.memberPermissions.filter(
            (p) => p !== permission
          ),
        },
      },
    });
    return response.data.removeReviewMemberPermission;
  },
  async removePermissionFromAll(context, { review, permission }) {
    const response = await apolloClient.mutate({
      mutation: RemoveReviewMemberPermissionFromAll,
      variables: { review: review.id, permission },
    });
    return response.data.removeReviewMemberPermissionFromAll;
  },
  async deleteReviewMember(context, reviewMember) {
    const response = await apolloClient.mutate({
      mutation: DeleteReviewMember,
      variables: { id: reviewMember.id },
      optimisticResponse: {
        deleteReviewMember: null,
      },
      update: (cache) => {
        cache.modify({
          id: cache.identify(reviewMember.review),
          fields: {
            reviewers(existingReviewers, { readField }) {
              return existingReviewers?.filter(
                (ref) => readField('id', ref) !== reviewMember.id
              );
            },
            reviewMembers(existingReviewMembers, { readField }) {
              return existingReviewMembers?.filter(
                (ref) => readField('id', ref) !== reviewMember.id
              );
            },
          },
        });
      },
    });

    return response.data.deleteReviewMember;
  },
};

const state = {};

const mutations = {};

export default {
  namespaced: true,
  actions,
  state,
  mutations,
};
